import apiClient from '../client';
import type {
  FarmInput,
  CreateFarmInputRequest,
  FarmInputUsageLogRequest,
  FarmInputUsageLogResponse,
  FarmInputRestockLogRequest,
  FarmInputRestockLogResponse,
  PaginatedResponse,
} from '../../types';

const farmInputService = {
  getAll: async (params: { page?: number; size?: number } = {}) => {
    const { page = 0, size = 50 } = params;
    const response = await apiClient.get<PaginatedResponse<FarmInput>>(
      `/farm-inputs?page=${page}&size=${size}&sort=name,asc`
    );
    return response.data;
  },

  getById: async (id: string) => {
    const response = await apiClient.get<FarmInput>(`/farm-inputs/${id}`);
    return response.data;
  },

  getByType: async (type: string, params: { page?: number; size?: number } = {}) => {
    const { page = 0, size = 50 } = params;
    const response = await apiClient.get<PaginatedResponse<FarmInput>>(
      `/farm-inputs/type/${type}?page=${page}&size=${size}`
    );
    return response.data;
  },

  getLowStock: async () => {
    const response = await apiClient.get<FarmInput[]>('/farm-inputs/low-stock');
    return response.data;
  },

  getExpiringSoon: async (daysAhead = 30) => {
    const response = await apiClient.get<FarmInput[]>(
      `/farm-inputs/expiring-soon?daysAhead=${daysAhead}`
    );
    return response.data;
  },

  create: async (data: CreateFarmInputRequest) => {
    const response = await apiClient.post<FarmInput>('/farm-inputs', data);
    return response.data;
  },

  update: async (id: string, data: Partial<CreateFarmInputRequest>) => {
    const response = await apiClient.put<FarmInput>(`/farm-inputs/${id}`, data);
    return response.data;
  },

  delete: async (id: string) => {
    await apiClient.delete(`/farm-inputs/${id}`);
  },

  recordUsage: async (id: string, data: FarmInputUsageLogRequest) => {
    const response = await apiClient.post<FarmInputUsageLogResponse>(
      `/farm-inputs/${id}/usage`,
      data
    );
    return response.data;
  },

  getUsageHistory: async (id: string, params: { page?: number; size?: number } = {}) => {
    const { page = 0, size = 20 } = params;
    const response = await apiClient.get<PaginatedResponse<FarmInputUsageLogResponse>>(
      `/farm-inputs/${id}/usage?page=${page}&size=${size}`
    );
    return response.data;
  },

  recordRestock: async (id: string, data: FarmInputRestockLogRequest) => {
    const response = await apiClient.post<FarmInputRestockLogResponse>(
      `/farm-inputs/${id}/restock`,
      data
    );
    return response.data;
  },

  getRestockHistory: async (id: string, params: { page?: number; size?: number } = {}) => {
    const { page = 0, size = 20 } = params;
    const response = await apiClient.get<PaginatedResponse<FarmInputRestockLogResponse>>(
      `/farm-inputs/${id}/restock?page=${page}&size=${size}`
    );
    return response.data;
  },

  search: async (params: { page?: number; size?: number; search?: string; type?: string; projectId?: string } = {}) => {
    const { page = 0, size = 10, search, type, projectId } = params;
    const qs = new URLSearchParams({ page: page.toString(), size: size.toString() });
    if (search) qs.set('search', search);
    if (type) qs.set('type', type);
    if (projectId) qs.set('projectId', projectId);
    const response = await apiClient.get<PaginatedResponse<FarmInput>>(`/farm-inputs/search?${qs}`);
    return response.data;
  },
};

export default farmInputService;
